import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaPaperclip, FaFilter, FaUsers, FaCalendarAlt } from "react-icons/fa";

const EventAttachments = () => {
  const navigate = useNavigate();
  const apiDomain = process.env.REACT_APP_API_DOMAIN;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const fetchEvents = async (filterVal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${apiDomain}/api/v2/attachment/events-with-required-files`,
        { params: { filter: filterVal } }
      );
      setEvents(res.data?.events || []);
    } catch (err) {
      console.error("Error fetching events with required files:", err);
      setError("Failed to load events. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents(filter);
  }, [filter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
          Upcoming
        </span>
      );
    } else if (now >= start && now <= end) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
          Ongoing
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
          Past
        </span>
      );
    }
  };

  return (
    <div className="lg:mr-[1vw] p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div className="flex items-center gap-3 mb-4 md:mb-0">
          <div className="bg-blue-100 p-3 rounded-xl">
            <FaPaperclip className="text-blue-600 text-xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Event Attachments
            </h1>
            <p className="text-sm text-gray-500">
              Events with required file uploads
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <FaFilter className="text-gray-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            <option value="all">All Events</option>
            <option value="ongoing">Ongoing</option>
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
          </select>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-500">Loading events...</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center text-red-600">
          {error}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && events.length === 0 && (
        <div className="bg-gray-50 rounded-lg p-10 text-center">
          <FaPaperclip className="text-gray-300 text-5xl mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">
            No events found with required files
          </p>
          <p className="text-gray-400 text-sm mt-1">
            Configure required files in the event form to see them here
          </p>
        </div>
      )}

      {/* Event Cards */}
      {!loading && !error && events.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {events.map((event) => (
            <div
              key={event.eventId}
              onClick={() =>
                navigate(`/event-attachments/${event.eventId}`, {
                  state: { eventName: event.eventName, requiredFiles: event.requiredFiles },
                })
              }
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer p-5"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-800 line-clamp-1">
                  {event.eventName || `Event #${event.eventId}`}
                </h3>
                {getStatusBadge(event.startDate, event.endDate)}
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <FaCalendarAlt className="text-gray-400" />
                <span>
                  {formatDate(event.startDate)} — {formatDate(event.endDate)}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                <FaUsers className="text-gray-400" />
                <span>
                  {event.submissionCount}{" "}
                  {event.submissionCount === 1 ? "submission" : "submissions"}
                </span>
              </div>

              {/* Required Files Tags */}
              {event.requiredFiles && Array.isArray(event.requiredFiles) && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {event.requiredFiles.map((file, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 text-xs rounded-full bg-purple-50 text-purple-600 border border-purple-200"
                    >
                      {file.name || file.type}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventAttachments;
